import mapKeys from 'lodash/mapKeys'
import snakeCase from 'lodash/snakeCase'

import { buildInstanceWanda, channelsInstance } from '@/api/instances'
import { addMixedDataLog } from '@/components/pillars/utils/addLog'

const instanceWanda = buildInstanceWanda()

const getPersonHealthEvents = async (
  _,
  { filters, sortBy, sortOrder, range }
) => {
  const result = await instanceWanda.get(`person_health_events`, {
    params: {
      filter: JSON.stringify(filters),
      sort: JSON.stringify([sortBy, sortOrder]),
      range: JSON.stringify(range)
    }
  })
  const healthEvents = await result

  return healthEvents
}

const getPersonHealthEventsByPerson = async (
  store,
  { filters, sortBy, sortOrder, range, personId }
) => {
  const result = await instanceWanda.get(
    `person_health_events/${personId}/events`,
    {
      params: {
        filter: JSON.stringify(filters),
        sort: JSON.stringify([sortBy, sortOrder]),
        range: JSON.stringify(range)
      }
    }
  )
  const healthEvents = await result

  if (healthEvents.length < range[1] - range[0]) {
    store.commit('setStopLoad', true)
  }

  store.commit('setRange', range)
  return healthEvents
}

const fetchCategories = async store => {
  try {
    if (store.state.categoryList.length) return

    const categoryList = await instanceWanda.get(
      `person_health_events/categories`
    )

    store.commit('setCategories', categoryList)
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'healthEvents/fetchCategories' },
      { root: true }
    )
  }
}

const fetchStatuses = async store => {
  try {
    if (store.state.statuses.length) return

    const statuses = await instanceWanda.get(`person_health_events/statuses`)

    store.commit('setStatuses', statuses)
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'healthEvents/fetchStatuses' },
      { root: true }
    )
  }
}

const getNewRange = ({ currRange, loadPrev, PAGINATION_LIMIT }) => {
  let newRange = []
  if (!currRange) {
    newRange = [0, PAGINATION_LIMIT - 1]
  } else {
    const init = loadPrev ? currRange[0] - PAGINATION_LIMIT : currRange[1] + 1
    const ends = loadPrev
      ? currRange[1] - PAGINATION_LIMIT
      : currRange[1] + PAGINATION_LIMIT

    newRange = [init, ends]
  }

  return newRange
}

const fetchHealthEventsByFilters = async (
  store,
  { globalLoading = true, loadPrev = false, paginationLimit } = {}
) => {
  const PAGINATION_LIMIT = paginationLimit || 10

  try {
    store.commit('setLoading', globalLoading)

    const filters = mapKeys(store.state.filters, (_, key) => snakeCase(key))
    const sortBy = snakeCase(store.state.sortBy)

    const newRange = getNewRange({
      currRange: store.state.range,
      loadPrev,
      PAGINATION_LIMIT
    })
    store.commit('setRange', newRange)

    const data = {
      filters,
      sortBy,
      sortOrder: store.state.sortOrder,
      range: newRange
    }
    const healthEvents = await store.dispatch('getPersonHealthEvents', data)

    if (loadPrev) {
      store.commit('setStopLoad', false)
    } else if (!loadPrev && healthEvents.length < PAGINATION_LIMIT) {
      store.commit('setStopLoad', true)
    }

    if (globalLoading) store.commit('setLoading', false)
    store.commit('setHealthEvents', healthEvents)
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'healthEvents/fetchHealthEventsByFilters' },
      { root: true }
    )
  }
}

const getFormattedLocalDate = () => {
  return new Date().toLocaleString('sv').replace(' ', 'T')
}

const convertDueDates = dueDates => {
  if (!dueDates?.length) return null
  const isLate = dueDates.some(dueDate => dueDate === 'LATE')
  const isOnTime = dueDates.some(dueDate => dueDate === 'ON_TIME')
  if (!isOnTime && isLate) return { second: getFormattedLocalDate() }
  if (isOnTime && !isLate) return { first: getFormattedLocalDate() }
  return null
}

const fetchPersonHealthEvents = async (
  store,
  { personId, status, categories, dueDates, sortOrder }
) => {
  const PAGINATION_LIMIT = 10

  store.commit('setHasErrorOnFetching', false)

  try {
    store.commit('sanitizeFilters', {
      filters: {
        status: status,
        category: !categories?.length ? null : categories,
        dueDate: convertDueDates(dueDates)
      }
    })

    const filters = mapKeys(store.state.filters, (_, key) => snakeCase(key))
    const sortBy = snakeCase(store.state.sortBy)

    const newRange = getNewRange({
      currRange: store.state.range,
      loadPrev: false,
      PAGINATION_LIMIT
    })
    store.commit('setRange', newRange)

    const data = {
      personId,
      filters,
      sortBy,
      sortOrder,
      range: store.state.range
    }
    const personHealthEvents = await store.dispatch(
      'getPersonHealthEventsByPerson',
      data
    )

    if (store.rootState.pillars.personId === personId) {
      store.commit('setPaginatedHealthEvents', personHealthEvents)
    } else {
      addMixedDataLog('fetchPersonHealthEvents')
    }
  } catch (error) {
    const undoRangeUpdate = () => {
      const newRange = getNewRange({
        currRange: store.state.range,
        loadPrev: true,
        PAGINATION_LIMIT
      })
      store.commit('setRange', newRange)
    }

    undoRangeUpdate()
    store.commit('setHasErrorOnFetching', true)

    store.dispatch(
      'errors/setError',
      { error, request: 'healthEvents/fetchPersonHealthEvents' },
      { root: true }
    )
  }
}

const fetchHealthEventById = async (store, eventId) => {
  try {
    const healthEvent = await instanceWanda.get(
      `person_health_events/${eventId}`
    )

    return healthEvent
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'healthEvents/fetchHealthEventById' },
      { root: true }
    )
  }
}

const getHealthEventById = async (store, eventId) => {
  try {
    store.commit('setLoading', true)
    const healthEvent = await store.dispatch('fetchHealthEventById', eventId)

    store.commit('setLoading', false)
    store.commit('setCurrentEvent', healthEvent)
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'healthEvents/getHealthEventById' },
      { root: true }
    )
  }
}

const updateHealthEventLocally = (store, data) => {
  store.commit('updateHealthEventLocally', data)
}

const updateHealthEvent = async (store, { data }) => {
  try {
    store.commit('setLoading', true)

    const poolTaskWithoutResponsible = data?.healthcareTeamId && !data?.staffId
    const loggedStaffId = store.rootState.user.user.id

    const result = await instanceWanda.put(`person_health_events/${data.id}`, {
      json: {
        ...data,
        staffId: poolTaskWithoutResponsible ? loggedStaffId : data.staffId
      }
    })

    store.commit('setLoading', false)
    return result
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'healthEvents/updateHealthEvent' },
      { root: true }
    )
  }
}

const createHealthEvent = async (store, data) => {
  try {
    const result = await instanceWanda.post('person_health_events', {
      json: data
    })

    return result
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'healthEvents/createHealthEvent' },
      { root: true }
    )
  }
}

const fetchFollowUpTypes = async (store, ids) => {
  try {
    const param = ids?.length > 0 ? `?ids=${ids}` : ''
    const followUpTypes = await channelsInstance.get(`channel_fup${param}`)

    return followUpTypes
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'healthEvents/fetchFollowUpTypes' },
      { root: true }
    )
  }
}

const fetchHealthCareTeam = async store => {
  try {
    const healthCareTeamList = await instanceWanda.get('healthcare_teams')

    store.commit('setHealthCareTeam', healthCareTeamList)
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'healthEvents/fetchHealthCareTeam' },
      { root: true }
    )
  }
}

const fetchPlanedFollowUps = async (store, channelId) => {
  try {
    const plannedFollowUps = await channelsInstance.get(
      `channels/${channelId}/follow_ups`
    )

    store.commit('setPlannedFollowUps', plannedFollowUps)
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'healthEvents/fetchPlanedFollowUps' },
      { root: true }
    )
  }
}

const getUserTasks = async (store, userId) => {
  try {
    const data = await instanceWanda.get(
      `person_health_events/${userId}/next_tasks`
    )

    return data
  } catch (error) {
    store.dispatch(
      'errors/setError',
      {
        error,
        request: 'healthEvents/getUserTasks',
        message: 'Falha ao carregar próximas tarefas para o usuário'
      },
      { root: true }
    )
  }
}

const getSuggestedCategoriesAndResponsible = async store => {
  try {
    const [suggestedCategories, suggestedResponsible] = await Promise.all([
      instanceWanda.get('person_health_events/suggested/categories'),
      instanceWanda.get('person_health_events/suggested/responsible')
    ])

    return { suggestedCategories, suggestedResponsible }
  } catch (error) {
    store.dispatch(
      'errors/setError',
      {
        error,
        request: 'healthEvents/getSuggestedCategoriesAndResponsible',
        message: 'Falha ao carregar sugestões de categoria e responsável'
      },
      { root: true }
    )

    return { suggestedCategories: [], suggestedResponsible: [] }
  }
}

const toggleSortOrder = store => {
  store.commit('toggleSortOrder')
}

const setSortBy = (store, field) => {
  store.commit('setSortBy', field)
}

const clearHealthEvents = store => {
  store.commit('setCategories', [])
  store.commit('setStatuses', [])
  store.commit('setHealthEvents', [])
  store.commit('setCurrentEvent', undefined)
  store.commit('setHealthCareTeam', [])
  store.commit('setPlannedFollowUps', [])
  store.commit('setFollowUpData', null)
}

const clearPagination = store => {
  store.commit('setRange', null)
  store.commit('setStopLoad', false)
}

const resetHealthEvents = store => {
  clearHealthEvents(store)
  clearPagination(store)
}

export default {
  getPersonHealthEvents,
  getPersonHealthEventsByPerson,
  fetchCategories,
  fetchStatuses,
  fetchHealthCareTeam,
  fetchHealthEventsByFilters,
  updateHealthEventLocally,
  updateHealthEvent,
  createHealthEvent,
  getHealthEventById,
  fetchHealthEventById,
  fetchPersonHealthEvents,
  fetchFollowUpTypes,
  fetchPlanedFollowUps,
  getUserTasks,
  getSuggestedCategoriesAndResponsible,
  clearHealthEvents,
  clearPagination,
  resetHealthEvents,
  toggleSortOrder,
  setSortBy
}
